/**
 * Channel entity class
 */

/**
 * ChannelFactory
 * returns Channel entity class
 *
 * @class ChannelFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.Channel} class
 *
 * @ngdoc factory
 * @name mwApp.factory:ChannelFactory
 */
/* @ngInject */
export default function ChannelFactory ($http, Entity, Catchup, Type, m2mMixin, onOffMixin, upDownMixin, FileUploadMixin, UrlPolicy, authSvc, alertLiteSvc) {
  /* === Channel class === */

  /**
   * Channel model
   *
   * @class Channel
   * @classdesc Channel model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @mixes models.m2mMixin
   * @mixes models.onOffMixin
   * @mixes models.upDownMixin
   * @mixes models.FileUploadMixin
   * @return {models.Channel} Channel entity
   */
  function Channel () {
    var obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  Channel.prototype = Object.create(Entity.prototype)
  Channel.prototype.constructor = Channel

  /* === Channel instance methods & fields === */

  Object.assign(Channel.prototype, {

    /**
     * instance fields
     *
     * @memberof models.Channel#
     * @type {Object}
     */
    _fields: {
      id: Number,
      name: String,
      $ageRatingType: 'default',
      $country: {
        id: Number,
        name: String,
        isoCode: String
      },
      ageRating: {
        id: Number,
        country: {
          id: Number,
          name: String,
          isoCode: String
        },
        sortOrder: Number,
        rating: String
      },
      catchupAllowed: Boolean,
      channelNumber: Number(1),
      channelType: String, // ENTITY_TYPES
      enabled: Boolean,
      chatAllowed: Boolean,
      dolbyAtmosAvailable: Boolean,
      epgId: Type(String('0')), // TODO empty string should be valid here!
      language: String,
      locked: Boolean,
      logoUrl: String,
      networkChannelLinks: ['NetworkChannelLink'],
      orderType: Type(String('Default')),
      resolution: 'HD',
      subscriber: 'Subscriber',
      timeshiftAllowed: Boolean,
      urls: {
        STB: 'MediaUrl',
        PC: 'MediaUrl',
        iOS: 'MediaUrl',
        Android: 'MediaUrl',
        SamsungTV: 'MediaUrl',
        Others: 'MediaUrl',
        WEB: 'MediaUrl'
      },
      defaultStream: 'DefaultStream',
      policedStreams: null, // 'PolicedStreamsFactory',
      streamPolicy: 'UrlPolicy',
      streamBindings: ['StreamBinding'],
      contentSets: ['ContentSet'],
      /* local variables */
      $defaultAgeRating: {
        id: Number,
        country: {
          id: Number,
          name: String,
          isoCode: String
        },
        sortOrder: Number,
        rating: String
      },
      $networksSets: {
        network: 'Network',
        channelSet: 'ChannelSet'
      },
      $itemType: 'channel',
      $contentSetType: 'vodSet',
      $contentSetName: 'VOD Set'
    },

    /**
     * url template
     *
     * @memberof models.Channel#
     * @type {String}
     */
    _urlTemplate: 'api/channels/{{::id}}',

    /**
     * public url template
     *
     * @memberof models.Channel#
     * @type {String}
     */
    _publicUrlTemplate: 'channels/{{::id}}',

    /**
     * Prepare channel
     *
     * @memberof models.Channel#
     * @override
     */
    _prepare: function () {
      UrlPolicy.prepareToLoadContentItem(this)

      // compatibility with old channels, when epgId was integer field
      // if (this.epgId === '0') {
      //   this.epgId = ''
      // }
      if ((!authSvc.hasEnabledFlag('WEB_PLATFORM_ENABLED')) && this.urls) {
        delete this.urls.WEB
      }

      if (this.ageRating) {
        if (!this.ageRating.country) {
          this.$ageRatingType = 'default'
          this.$defaultAgeRating = this.ageRating
          this.ageRating = {}
        } else {
          this.$ageRatingType = 'countryBased'
          this.$country = this.ageRating.country
          this.$defaultAgeRating = {}
        }
      }
    },

    /**
     * Get underlying object
     *
     * @memberof models.Channel#
     * @return {Object} new underlying object as plain object
     * @override
     */
    _object: function () {
      UrlPolicy.prepareToSaveContentItem(this)

      this.channelNumber = this.channelNumber ? this.channelNumber : 1
      this.ageRating = this.$ageRatingType === 'default' ? this.$defaultAgeRating : this.ageRating
      if (this.ageRating) {
        delete this.ageRating.country
      } else {
        this.ageRating = { id: 1, rating: '3+' }
      }
      // fix epgId //TODO remove when epgId will not be mandatory field on backend
      if (!this.epgId) {
        this.epgId = '0'
      }

      return Entity.prototype._object.apply(this, arguments)
    },

    /**
     * Get logo URL for given size
     *
     * @memberof models.Channel#
     * @param  {String} size - e.g. '53x31'
     * @return {String}
     */
    _logoUrlFor: function (size) {
      return this.logoUrl
        ? this.logoUrl.replace('{{size}}', size)
        : ''
    },

    /**
     * Get service extra for channel
     *
     * @memberof models.Channel#
     * @private
     */
    _loadServices: function () {
      return authSvc.hasAccess('viewChannelServices') ? Channel.load({
        id: this.id,
        url: '\\services'
      }, {
        liveRewind: 'LiveRewindChannel',
        catchups: ['CatchupChannel']
      }).catch(() => {
        alertLiteSvc.alertError('Services Unavailable')
        return null
      }) : null
    },

    /**
     * Patch method
     *
     * @memberof models.Episode#
     * @param {String} url
     * @param params
     * @return {Promise} promise
     * @private
     */
    _patch: function (url, params) {
      return $http.patch(url, params)
        .then(
          response => {
            if (response.data) {
              this._weakCopy(response.data)
            }
            return response
          }
        ).catch(
          rejection => {
            return alertLiteSvc.alertError(rejection)
          })
    }

  }, m2mMixin('networks'), onOffMixin, upDownMixin('channelNumber'), FileUploadMixin)

  /**
   * Search content by param
   *
   * @param {String} url
   * @param {Object} tableState
   * @returns {Promise} promise
   */
  Channel.search = function (url, tableState) {
    const params = {
      page: tableState.page || 0,
      count: 25
    }
    if (Object.prototype.hasOwnProperty.call(tableState, 'q')) {
      params.q = tableState.q
    }
    return $http.get(url, { params })
      .data()
      .then((response) => response.payload)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  /* === Channel static methods & fields === */

  // copy all static methods from Entity class
  Object.assign(Channel, Entity)
  Channel._name = 'Channel'
  Channel.init()

  // permissions
  Channel.access = {
    view: 'viewChannel createCatchup | editCatchup | editNetwork | editCategory |',
    create: 'createChannel',
    edit: 'editChannel',
    delete: 'deleteChannel'
  }

  // extra
  Channel.extra = {
    acls: [{ id: Number, name: String }],
    languages: [String],
    networks: ['Network'],
    playbackTokenGenerators: ['PlaybackTokenGenerator'],
    drmProfiles: ['DRMProfile'],
    channelSets: []
  }

  return Channel
}
